import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, MapPin } from "lucide-react";
import { scrollToId, waLink } from "@/lib/site";
import { WhatsAppIcon } from "./WhatsAppIcon";

const ease = [0.22, 1, 0.36, 1];
const LINES = [
  { text: "Precision cut", cls: "text-[#0e1116]" },
  { text: "in iron.", cls: "text-silver-grad" },
  { text: "Carved in teak.", cls: "text-gold-grad" },
];

const HeroLine = ({ line, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${line.cls}`} initial={{ y: "110%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease }}>
      {line.text}
    </motion.span>
  </span>
);

const HeroVisual = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} className="relative [perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]"
        initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.3, ease }}>
        <div className="chamfer relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden bg-[#0e1116]">
          <motion.img style={{ y, scale: 1.18 }} src="/media/hero.jpg" alt="Sparks from metal cutting in a workshop (illustrative)"
            className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_40%,transparent_20%,rgba(14,17,22,0.75)_85%)]" />
          <span className="absolute bottom-4 left-14 font-mono-tech text-[11px] tracking-[0.2em] uppercase text-white/80">Illustrative image</span>
        </div>
        <div style={{ transform: "translateZ(60px)" }}
          className="absolute -left-4 sm:-left-10 top-8 bg-white/80 backdrop-blur-xl border border-white shadow-[0_30px_60px_-20px_rgba(14,17,22,0.35)] p-3 chamfer-sm">
          <img src="/brand/tsr-logo-600.jpg" alt="TSR Industry logo" className="h-24 w-24 sm:h-32 sm:w-32 object-cover" />
        </div>
        <div style={{ transform: "translateZ(40px)" }}
          className="absolute -right-2 sm:-right-6 bottom-10 bg-[#0e1116] text-white px-5 py-4 chamfer-sm shadow-2xl">
          <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#e8c36a]">Metal + Wood</p>
          <p className="font-display text-lg font-semibold mt-1">One workshop.</p>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative overflow-hidden blueprint pt-28 sm:pt-32 lg:pt-36 pb-16 lg:pb-24">
    <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(212,166,58,0.22),transparent_65%)]" />
    <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
      <div className="lg:col-span-7">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/70 px-3.5 py-1.5 font-mono-tech text-[11px] sm:text-xs uppercase tracking-[0.2em] text-slate-700">
          <MapPin className="h-3.5 w-3.5 text-[var(--gold)]" /> Machilipatnam, Andhra Pradesh
        </motion.p>
        <h1 className="font-display font-extrabold tracking-[-0.035em] leading-[0.92] mt-7 text-[2.9rem] sm:text-6xl lg:text-7xl xl:text-[5.4rem]">
          {LINES.map((l, i) => <HeroLine key={l.text} line={l} i={i} />)}
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.8, ease }}
          className="mt-8 max-w-xl text-base md:text-lg text-slate-700 leading-relaxed">
          TSR Industry delivers CNC machining, turning and milling, iron, plasma and laser cutting, CNC wood routing and custom
          fabrication — alongside teak lotus flowers, pooja mandirs, wooden panels and furniture.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.95, ease }}
          className="mt-10 flex flex-col sm:flex-row gap-3">
          <button data-testid="hero-quote-btn" onClick={() => scrollToId("quote")}
            className="group inline-flex items-center justify-center gap-3 h-14 px-8 rounded-full bg-[#0e1116] text-white font-semibold hover:bg-black transition-colors">
            Request a Quotation
            <span className="grid place-items-center h-8 w-8 rounded-full bg-gold-grad text-[#0e1116] transition-transform duration-300 group-hover:rotate-[-45deg]">
              <ArrowDownRight className="h-4 w-4" />
            </span>
          </button>
          <a data-testid="hero-whatsapp-btn" href={waLink()} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2.5 h-14 px-8 rounded-full bg-[#25D366] text-[#0b2e17] font-semibold hover:brightness-95 transition-[filter]">
            <WhatsAppIcon /> WhatsApp Enquiry
          </a>
        </motion.div>
      </div>
      <div className="lg:col-span-5 px-4 sm:px-8 lg:px-0">
        <HeroVisual />
      </div>
    </div>
  </section>
);
